import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Alert, Group, Progress, Stack, Text } from '@mantine/core';
import { Boxes, Plus, Server } from 'lucide-react';
import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageContainer,
  PageHeader,
  StatusDot,
  Table,
  type SortDirection,
  type TableColumn,
} from '../components/ui';
import { cardItem } from '../lib/motion';
import { useRanges } from '../hooks/useRanges';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import type { RangeStatus, RangeSummary } from '../api/types';

// `bar` is the progress colour. It used to be hardcoded `neon` for every row,
// so a range under attack, one finished, and one still provisioning all drew
// the same green — the bar said "how far", never "how it is going", and an
// operator scanning the table could not tell a healthy run from a failed one
// without reading every badge. Tying it to the status makes the two encodings
// agree.
const RANGE_STATUS: Record<
  RangeStatus,
  {
    variant: 'success' | 'info' | 'warn' | 'danger' | 'neutral';
    dot: 'running' | 'idle' | 'error' | 'warning';
    bar: string;
  }
> = {
  pending: { variant: 'neutral', dot: 'idle', bar: 'gray' },
  provisioning: { variant: 'info', dot: 'running', bar: 'cyan' },
  ready: { variant: 'success', dot: 'running', bar: 'neon' },
  attacking: { variant: 'warn', dot: 'warning', bar: 'orange' },
  completed: { variant: 'success', dot: 'idle', bar: 'neon' },
  failed: { variant: 'danger', dot: 'error', bar: 'red' },
  destroying: { variant: 'warn', dot: 'warning', bar: 'orange' },
  destroyed: { variant: 'neutral', dot: 'idle', bar: 'gray' },
  hibernated: { variant: 'info', dot: 'idle', bar: 'cyan' },
};

export const Route = createFileRoute('/ranges/')({
  // Accept ?destroyed=<id> so the range-detail page can hand off a "destroyed"
  // confirmation after teardown (see ranges.$id.tsx destroy flow).
  validateSearch: (search: Record<string, unknown>): { destroyed?: string } =>
    typeof search.destroyed === 'string' && search.destroyed ? { destroyed: search.destroyed } : {},
  component: RangesIndex,
});

function RangesIndex() {
  const { data, isPending, isError, error, refetch } = useRanges();
  const navigate = useNavigate();
  const { destroyed } = Route.useSearch();
  const isTrainee = useAuthStore(selectRole) === 'trainee';
  // Client-side sort — an operator with 20 ranges needs to find the expensive
  // or failed one. The Table primitive already supported this; no column had
  // ever opted in.
  const [sort, setSort] = useState<{ key: string; direction: SortDirection }>({
    key: 'id',
    direction: null,
  });

  const sorted = useMemo(() => {
    const rows = [...(data ?? [])];
    if (!sort.direction) return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const value = (r: RangeSummary): string | number => {
      switch (sort.key) {
        case 'status':
          return r.status;
        case 'cost':
          return r.costSoFar;
        case 'vms':
          return r.vmCount;
        default:
          return r.id;
      }
    };
    return rows.sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [data, sort]);

  const columns: ReadonlyArray<TableColumn<RangeSummary>> = useMemo(
    () => [
      {
        key: 'id',
        header: 'Range',
        sortable: true,
        cell: (r) => (
          // Kept as a real <Link> so middle-click / open-in-new-tab still work;
          // the surrounding row is separately clickable (see onRowClick).
          <Link
            to="/ranges/$id"
            params={{ id: r.id }}
            style={{ textDecoration: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack gap={0}>
              <Text className="font-mono" size="sm" c="rgb(var(--c-txt-bright))">
                {r.id}
              </Text>
              <Text size="xs" c="dimmed">
                {r.scenarioName}
              </Text>
            </Stack>
          </Link>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        cell: (r) => {
          // Fallback so an unmapped/unexpected status can never crash the page
          // (e.g. backend 'destroying' before this map knew about it).
          const meta = RANGE_STATUS[r.status] ?? { variant: 'neutral' as const, dot: 'idle' as const };
          return (
            <Group gap="xs" wrap="nowrap">
              <StatusDot state={meta.dot} size="sm" />
              <Badge variant={meta.variant} size="xs">
                {r.status}
              </Badge>
            </Group>
          );
        },
      },
      {
        key: 'phase',
        header: 'Phase',
        cell: (r) => (
          <Text size="sm" c={r.currentPhase ? 'rgb(var(--c-txt-bright))' : 'dimmed'}>
            {r.currentPhase ?? '—'}
          </Text>
        ),
      },
      {
        key: 'progress',
        header: 'Progress',
        cell: (r) => {
          const pct = Math.round(r.progress * 100);
          return (
            <Group gap="xs" wrap="nowrap">
              <Progress
                value={pct}
                size="sm"
                radius="xl"
                color={(RANGE_STATUS[r.status] ?? RANGE_STATUS.pending).bar}
                aria-label={`${pct}%`}
                style={{ flex: 1, minWidth: 64 }}
              />
              {/* The bar alone reads as a vague amount. A number makes the row
                  comparable to the one above it at a glance. */}
              <Text size="xs" c="dimmed" className="font-mono" style={{ width: 34, textAlign: 'right' }}>
                {pct}%
              </Text>
            </Group>
          );
        },
        width: 170,
      },
      {
        key: 'vms',
        header: 'VMs',
        align: 'right',
        sortable: true,
        cell: (r) => (
          <Text size="sm" className="font-mono">
            {r.vmCount}
          </Text>
        ),
        width: 80,
      },
      {
        key: 'cost',
        header: 'Cost',
        align: 'right',
        sortable: true,
        // Cost was drawn in an accent colour, which in light mode rendered
        // orange-red and read as a warning. Spend is information, not an alarm.
        cell: (r) => (
          <Text size="sm" className="font-mono" c="rgb(var(--c-txt-bright))">
            ${r.costSoFar.toFixed(2)}
          </Text>
        ),
        width: 100,
      },
    ],
    [],
  );

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Boxes}
        title="Ranges"
        description={
          isTrainee
            ? 'Cyber-range environments your instructor has deployed for you. Open one to jump into the console.'
            : 'Deployed cyber-range instances for your tenant. Hibernate keeps state at low cost; destroy tears down to $0/hr.'
        }
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Ranges' }]}
        {...(isTrainee
          ? {}
          : {
              actions: (
                <Button
                  variant="primary"
                  leftIcon={<Plus className="size-4" />}
                  onClick={() => navigate({ to: '/scenarios' })}
                >
                  Deploy range
                </Button>
              ),
            })}
      />

      {destroyed && (
        <Alert
          icon={<Server size={16} />}
          color="green"
          variant="light"
          title="Range destroyed"
          withCloseButton
          onClose={() => navigate({ to: '/ranges', search: {} })}
          mb="md"
        >
          Range <span className="font-mono">{destroyed}</span> has been torn down.
        </Alert>
      )}

      {isError ? (
        <ErrorState title="Could not load ranges" error={error} onRetry={() => void refetch()} />
      ) : !isPending && data && data.length === 0 ? (
        <EmptyState
          illustration={<Boxes size={48} />}
          title="No ranges yet"
          description="Deploy a scenario from the catalog — your range will appear here while it provisions."
          cta={{
            // navigate(), not window.location.href — a full document reload
            // throws away the SPA cache and the rehydrated auth store.
            label: 'Browse scenarios',
            onClick: () => void navigate({ to: '/scenarios' }),
          }}
        />
      ) : (
        <motion.div variants={cardItem} initial="hidden" animate="show">
          <Card>
            <Stack gap="md">
              <Group gap="sm">
                <Server size={16} />
                <Text size="sm" fw={600}>
                  Active ranges ({isPending ? '…' : (data?.length ?? 0)})
                </Text>
              </Group>
              <Table
                columns={columns}
                rows={sorted}
                rowKey={(r) => r.id}
                loading={isPending}
                skeletonRows={6}
                sort={sort}
                onSortChange={setSort}
                onRowClick={(r) => void navigate({ to: '/ranges/$id', params: { id: r.id } })}
                rowLabel={(r) => `Open range ${r.id} (${r.scenarioName})`}
              />
            </Stack>
          </Card>
        </motion.div>
      )}
    </PageContainer>
  );
}
